import { StoreResponse, StoreScheduleResponse } from '@ydistri/api-sdk';
import { formatDate } from '@ydistri/generic-utils';
import { addDays, format, parseISO } from 'date-fns';

export const VOCABULARY_NOTICE =
  'The period is when the store stays out of the redistribution proposal — not the ' +
  "store's own closure period.";

/**
 * A restriction exists exactly when its two dates are set; there is no separate flag for it.
 *
 * These are date-only values, so they are parsed to local midnight before formatting - handing
 * the string straight to formatDate would read it as UTC and render the previous day west of it.
 */
export const formatPeriod = (from?: string | null, to?: string | null): string =>
  from && to ? `${formatDate(parseISO(from))} – ${formatDate(parseISO(to))}` : '';

/** Names what a record restricts, for places that show a record without its own row. */
export const describeRestrictions = (record: StoreScheduleResponse): string => {
  const parts: string[] = [];
  const sending = formatPeriod(record.cannotSendFrom, record.cannotSendTo);
  const receiving = formatPeriod(record.cannotReceiveFrom, record.cannotReceiveTo);
  if (sending !== '') parts.push(`cannot send ${sending}`);
  if (receiving !== '') parts.push(`cannot receive ${receiving}`);
  return parts.join(', ');
};

export const restrictsSending = (record: StoreScheduleResponse): boolean =>
  !!record.cannotSendFrom && !!record.cannotSendTo;

export const restrictsReceiving = (record: StoreScheduleResponse): boolean =>
  !!record.cannotReceiveFrom && !!record.cannotReceiveTo;

export const onlyInEffect = (records: StoreScheduleResponse[]): StoreScheduleResponse[] =>
  records.filter(record => record.isInEffect);

const STARTS_SOON_DAYS = 7;

const startsWithin = (
  from: string | null | undefined,
  to: string | null | undefined,
  after: string,
  until: string,
): boolean => !!from && !!to && from > after && from <= until;

/**
 * The DataTable row class: red for a record in effect on the evaluation date, orange for one whose
 * period starts within the next week. The window end is a local calendar date - toISOString would
 * shift it a day back east of UTC.
 */
export const getStoreScheduleRowClassName = (record: StoreScheduleResponse): string => {
  if (record.isArchived) return 'grey';
  if (record.isInEffect) return 'red';

  const today = record.evaluatedOn;
  const until = format(addDays(parseISO(today), STARTS_SOON_DAYS), 'yyyy-MM-dd');
  return startsWithin(record.cannotSendFrom, record.cannotSendTo, today, until) ||
    startsWithin(record.cannotReceiveFrom, record.cannotReceiveTo, today, until)
    ? 'orange'
    : '';
};

/**
 * Matches every store identifier the table can show; the system id is shown, and so matched, only
 * on a project whose warehouse id is an auto-increment INT.
 */
export const matchesSearch = (
  record: StoreScheduleResponse,
  search: string,
  matchSystemId = false,
): boolean => {
  const needle = search.trim().toLowerCase();
  if (needle === '') return true;
  const { store } = record;
  return [
    store.name,
    store.code,
    store.customerStoreId,
    matchSystemId ? String(store.id) : null,
    record.note,
  ].some(field => field?.toLowerCase().includes(needle));
};

export const createdAtSorter = (
  left: StoreScheduleResponse,
  right: StoreScheduleResponse,
): number => right.createdAt.localeCompare(left.createdAt);

/** The table's default order: stores as everywhere else (`compareStores`), a store's newest record first. */
export const storeThenNewestSorter =
  (compareStores: (left: StoreResponse, right: StoreResponse) => number) =>
  (left: StoreScheduleResponse, right: StoreScheduleResponse): number =>
    compareStores(left.store, right.store) || createdAtSorter(left, right);

/**
 * Overlapping records for one store are allowed, so more than one active record is a warning
 * rather than an error. Only active records count - an archived one no longer competes.
 */
export const getActiveSiblings = (
  record: StoreScheduleResponse,
  all: StoreScheduleResponse[],
): StoreScheduleResponse[] =>
  record.isActive
    ? all.filter(
        candidate =>
          candidate.isActive &&
          candidate.store.id === record.store.id &&
          candidate.storeScheduleId !== record.storeScheduleId,
      )
    : [];
